import { useCallback, useEffect, useRef, useState } from 'react';
import { type Id, toast } from 'react-toastify';
import type { BowtieRequestWithId } from '../api/@types/enhanced-bowtie-api.types';
import { useBowtieSuggestJobPostMutation } from '../api/enhanced/enhanced-bowtie-api';
import type { BowtieSuggestPostApiArg, BowtieSuggestPostApiResponse } from '../api/generated/bowtie-api';
import { useGetAIBackgroundJobResult } from './use-get-ai-background-job-result';

/**
 * Custom hook to handle AI analysis for bowtie data.
 *
 * @param {Function} onAIAnalysisReady - Callback function to be called when AI analysis is ready. Receives the analysis text as an argument.
 *
 * @returns {Object} - An object containing:
 *   - `runAIAnalysis` (Function): Function to trigger the AI analysis.
 *   - `isLoading` (boolean): Indicates whether the AI analysis is currently loading.
 *
 * @example
 * const { runAIAnalysis, isLoading } = useAIAnalysis((text) => {
 *   console.log('AI Analysis Result:', text);
 * });
 *
 * // Trigger AI analysis
 * runAIAnalysis();
 */
export const useAIAnalysis = (onAIAnalysisReady: (text: string) => void, flowBowtieDto: BowtieRequestWithId) => {
  const toastId = useRef<Id | undefined>(undefined);
  const [jobId, setJobId] = useState<string>();

  const [triggerSuggestPostJob, { isLoading }] = useBowtieSuggestJobPostMutation();

  const {
    data: backgroundJobResult,
    loading: backgroundJobLoading,
    error: backgroundJobError,
  } = useGetAIBackgroundJobResult({
    jobId: jobId!,
    setJobId: setJobId,
  });

  const loading = isLoading || backgroundJobLoading;

  const dismissToast = useCallback(() => {
    toast.dismiss(toastId.current);
    setJobId(undefined);
  }, []);

  useEffect(() => {
    if (backgroundJobResult && !loading) {
      const data = backgroundJobResult as unknown as BowtieSuggestPostApiResponse;
      onAIAnalysisReady(data.suggestion_text);
      dismissToast();
      toast.info('AI Analysis Complete', { autoClose: 1200 });
    } else if (backgroundJobError && !loading) {
      dismissToast();
      toast.error('Unable to generate ai suggestion, please try again later.');
    }
  }, [loading, backgroundJobResult, backgroundJobError]);

  const handleBowtieAIAnalysisClick = useCallback(async () => {
    const payload = generateFlowSuggestPayload(flowBowtieDto);
    toastId.current = toast.info('Running AI Analysis...', { autoClose: false, toastId: toastId.current });
    triggerSuggestPostJob(payload)
      .unwrap()
      .then((jobId) => setJobId(jobId))
      .catch(() => {
        dismissToast();
        toast.error('Unable to generate ai suggestion, please try again later.');
      });
  }, [flowBowtieDto]);

  return {
    runAIAnalysis: handleBowtieAIAnalysisClick,
    isLoading: loading,
  };
};

// util
const generateFlowSuggestPayload = (bowtieData: BowtieRequestWithId, term?: string): BowtieSuggestPostApiArg => {
  return {
    bowtieCorrectionRequest: {
      bowtie: {
        ...bowtieData,
        term: term ?? '',
      },
      control_types: [''],
    },
  };
};
